/**
 * Session state.
 *
 * `GET /api/me` is the bootstrap call (API-CONTRACT §2) and the single source of truth for
 * "who is this, do they have a Construct, are they entitled, are they an operator". Everything
 * downstream reads it rather than refetching.
 *
 * A signed-in account with NO Construct is a first-class state (contract rule 4), so `me` being
 * present with `deployment: null` is normal — not an error, not a redirect loop.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiError, api } from "../api/client.js";
import type { Me } from "../api/types.js";

interface SessionValue {
  me: Me | null;
  loading: boolean;
  error: unknown;
  /** Refetch /api/me — call after checkout, provisioning completion, or sign-out. */
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const next = await api.me(signal);
      setMe(next);
      setError(null);
    } catch (e) {
      if ((e as Error).name === "AbortError") return;
      // 401 is not an error state — it is the signed-out state.
      if (e instanceof ApiError && e.isAuth) {
        setMe(null);
        setError(null);
      } else {
        setError(e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const value = useMemo<SessionValue>(
    () => ({
      me,
      loading,
      error,
      refresh: async () => {
        await load();
      },
      signOut: async () => {
        try {
          await api.signOut();
        } finally {
          setMe(null);
        }
      },
    }),
    [me, loading, error, load],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside <SessionProvider>");
  return ctx;
}

/**
 * Where a signed-in customer belongs right now — Appendix B of the API contract, in one place
 * so no screen has to reimplement the mapping.
 */
export function homeRouteFor(me: Me | null): string {
  if (!me) return "/sign-in";
  const d = me.deployment;
  if (!d) return "/plans";
  switch (d.status) {
    case "AWAITING_PAYMENT":
      return "/plans";
    case "QUEUED":
    case "PROVISIONING":
    case "VERIFYING":
      return "/building";
    case "PROVISIONING_FAILED":
    case "VERIFYING_FAILED":
      return "/building";
    default:
      return "/dashboard";
  }
}
